import { useNavigate } from 'react-router-dom';
import { Brain, Library, Puzzle, MessageCircle } from 'lucide-react';
import { GradientTitle } from '../../components/ui';
import { CategoryCard } from '../../components/home/CategoryCard';
import { Helmet } from 'react-helmet-async';

interface PracticePageProps {}

export const PracticePage: React.FC<PracticePageProps> = () => {
  const navigate = useNavigate();

  interface PracticeOption {
    title: string;
    icon: any;
    description: string;
    path?: string;
    externalLink?: string;
  }

  const practiceOptions: PracticeOption[] = [
    {
      title: "AI Cognitive Bias Coach",
      icon: MessageCircle,
      description: "Practice debiasing techniques through interactive role-play scenarios with our new AI feature and explore custom GPTs.",
      path: "/practice/role-play-debias/"
    },
    {
      title: "Interactive Tests",
      icon: Brain,
      description: "Challenge yourself with multiple-choice questions about different cognitive biases.",
      path: "/practice/test/"
    },
    {
      title: "Flipcards",
      icon: Library,
      description: "Learn and memorize cognitive biases using interactive flashcards.",
      path: "/practice/flipcards/"
    },
    {
      title: "Match Game",
      icon: Puzzle,
      description: "Match examples with their corresponding cognitive biases in this interactive game.",
      path: "/practice/match/"
    }
  ];

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Practice Cognitive Bias Recognition | Interactive Exercises</title>
        <meta name="description" content="Strengthen your bias recognition skills with our interactive exercises. Practice identifying cognitive biases through quizzes, flashcards, and games." />
        <meta name="keywords" content="cognitive bias practice, bias recognition, critical thinking exercises, psychology practice, interactive learning" />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/practice/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Practice Cognitive Bias Recognition | Interactive Exercises" />
        <meta property="og:description" content="Strengthen your bias recognition skills with our interactive exercises. Practice identifying cognitive biases through quizzes, flashcards, and games." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/practice/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Practice Cognitive Bias Recognition | Interactive Exercises" />
        <meta name="twitter:description" content="Strengthen your bias recognition skills with our interactive exercises. Practice identifying cognitive biases through quizzes, flashcards, and games." />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>

      <div className="min-h-screen bg-orange-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
              Choose Your Practice Mode
            </GradientTitle>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Reinforce your understanding of cognitive biases through interactive exercises and games.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-8">
            {practiceOptions.map((option) => (
              <CategoryCard
                key={option.title}
                title={option.title}
                icon={option.icon}
                description={option.description}
                onClick={() => {
                  if (option.externalLink) {
                    window.open(option.externalLink, "_blank");
                  } else if (option.path) {
                    const path = option.path.endsWith('/') ? option.path : `${option.path}/`;
                    navigate(path);
                  }
                }}
                category=""
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}; 